/**
 * GameState.ts - Generic State Manager
 *
 * EventEmitter-based singleton state management with
 * optional localStorage persistence. Works in both browser and Node.js.
 */

type Listener<T> = (state: T, prev: T) => void;

export interface GameStateOptions {
  /** Disable localStorage persistence (for headless/testing) */
  headless?: boolean;
}

export class GameState<T extends object> {
  private state: T;
  private listeners: Set<Listener<T>> = new Set();
  private saveKey: string;
  private initialState: T;
  private headless: boolean;

  constructor(initialState: T, saveKey: string, options?: GameStateOptions) {
    this.saveKey = saveKey;
    this.headless = options?.headless ?? false;
    this.initialState = { ...initialState };
    this.state = this.load() ?? { ...initialState };
  }

  get(): T {
    return this.state;
  }

  set(newState: T): void {
    const prev = this.state;
    this.state = newState;
    this.save();
    this.listeners.forEach((fn) => fn(newState, prev));
  }

  patch(partial: Partial<T>): void {
    this.set({ ...this.state, ...partial });
  }

  subscribe(listener: Listener<T>): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  reset(overrides?: Partial<T>): void {
    if (!this.headless) {
      try { localStorage.removeItem(this.saveKey); } catch { /* */ }
    }
    const fresh = { ...this.initialState, ...overrides };
    const prev = this.state;
    this.state = fresh;
    this.listeners.forEach((fn) => fn(fresh, prev));
  }

  private save(): void {
    if (this.headless) return;
    try {
      localStorage.setItem(this.saveKey, JSON.stringify(this.state));
    } catch {
      // Storage full or unavailable - silently ignore
    }
  }

  private load(): T | null {
    if (this.headless) return null;
    try {
      const raw = localStorage.getItem(this.saveKey);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }
}
